import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, FileCheck2, Loader2, ShieldCheck } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { PageHeader } from "@/components/app-shell";
import { Button } from "@/design-system/primitives";
import {
  useMarketplace,
  useMarketplacePurchase,
  useResumeTreatmentOffer,
  useTreatmentOffers,
} from "@/lib/api/hooks";
import { ApiError } from "@/lib/api/client";
import { CarePurchaseNote } from '@/features/onboarding/CarePurchaseNote';
import { PaymentMethodPicker } from "@/features/marketplace/components/PaymentMethodPicker";
import {
  FREQUENCY_CYCLE_LABELS,
  FREQUENCY_LABELS,
  offerableFrequencies,
  type PlanFrequency,
} from "@/features/marketplace/stores/cart";
import {
  marketplaceProductForTreatmentOffer,
  canResumeTreatmentOffer,
  treatmentOfferCartProduct,
  treatmentOfferPurchasePayload,
} from "./treatmentOfferPurchase";

function money(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function TreatmentOfferScreen() {
  const { offerId } = useParams();
  return <TreatmentOfferCheckout key={offerId} offerId={offerId} />;
}

function TreatmentOfferCheckout({ offerId }: { offerId: string | undefined }) {
  const offers = useTreatmentOffers();
  const offer = offers.data?.offers.find((item) => item.id === offerId);
  const recoveringPurchase = canResumeTreatmentOffer(offer);
  const marketplace = useMarketplace(Boolean(offer && !recoveringPurchase));
  const purchase = useMarketplacePurchase();
  const resume = useResumeTreatmentOffer();
  const [frequency, setFrequency] = useState<PlanFrequency>("monthly");
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [completedOrderId, setCompletedOrderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [purchaseNeedsVerification, setPurchaseNeedsVerification] =
    useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(false);
  const product = marketplaceProductForTreatmentOffer(
    marketplace.data?.products ?? [],
    offer,
  );
  const cartProduct = product ? treatmentOfferCartProduct(product) : null;
  const frequencies = useMemo(
    () => (cartProduct ? offerableFrequencies(cartProduct) : []),
    [cartProduct],
  );

  useEffect(() => {
    if (!frequencies.length || frequencies.includes(frequency)) return;
    setFrequency(frequencies[0]);
  }, [frequencies, frequency]);

  const verifyPurchaseState = async () => {
    setCheckingPurchase(true);
    try {
      const refreshed = await offers.refetch();
      if (!refreshed.data || refreshed.error) {
        throw new Error("Purchase status is not available yet.");
      }
      setPurchaseNeedsVerification(false);
    } catch {
      setPurchaseNeedsVerification(true);
      setError(
        "We could not verify the original purchase yet. Check again or contact the care team before starting another purchase.",
      );
    } finally {
      setCheckingPurchase(false);
    }
  };

  if (purchaseNeedsVerification && !completedOrderId) {
    return (
      <div className="max-w-[64rem] mx-auto px-15 md:px-40 py-40">
        <section className="border border-info/30 bg-info-wash p-20">
          <h1 className="type-heading-lg">Checking the original purchase</h1>
          <p className="type-body-sm text-ink-secondary mt-8">
            The last response did not confirm completion. We’ll verify its saved
            status before allowing another payment choice.
          </p>
          {error && (
            <p role="alert" className="type-body-sm text-danger mt-10">
              {error}
            </p>
          )}
          <div className="mt-15 flex flex-wrap gap-8">
            <Button
              type="button"
              loading={checkingPurchase}
              onClick={() => void verifyPurchaseState()}
            >
              Check again
            </Button>
            <Button asChild variant="secondary">
              <Link to="/app/clinic?message=1">Message care team</Link>
            </Button>
          </div>
        </section>
      </div>
    );
  }

  if (
    !completedOrderId &&
    (offers.isLoading || (!recoveringPurchase && marketplace.isLoading))
  ) {
    return (
      <div className="min-h-[30rem] flex items-center justify-center gap-8 text-ink-muted type-body-sm">
        <Loader2 size={18} className="animate-spin" /> Loading your clinician’s
        treatment approval…
      </div>
    );
  }

  if (
    !completedOrderId &&
    (offers.isError || (!recoveringPurchase && marketplace.isError))
  ) {
    const failure = offers.error ?? marketplace.error;
    return (
      <div className="max-w-[64rem] mx-auto px-15 md:px-40 py-40">
        <div
          role="alert"
          className="border border-danger/30 bg-danger-wash p-20 text-center"
        >
          <h1 className="type-heading-lg">Treatment approval did not load</h1>
          <p className="type-body-sm text-ink-secondary mt-6">
            {failure instanceof Error
              ? failure.message
              : "The treatment catalog is temporarily unavailable. Your approval is still saved."}
          </p>
          <Button
            type="button"
            className="mt-15"
            loading={offers.isFetching || marketplace.isFetching}
            onClick={() => {
              void Promise.all([offers.refetch(), marketplace.refetch()]);
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (completedOrderId) {
    return (
      <div className="max-w-[64rem] mx-auto px-15 md:px-40 py-40">
        <div className="border border-success/30 bg-success-wash p-20 text-center">
          <CheckCircle2 size={28} className="mx-auto text-success" />
          <h1 className="type-heading-lg mt-10">Treatment purchased</h1>
          <p className="type-body-sm text-ink-secondary mt-6">
            Your clinician’s existing prescription is linked to the order. You
            do not need to repeat the intake or visit for this approval.
          </p>
          <Button asChild className="mt-15">
            <Link
              to={
                completedOrderId === "complete"
                  ? "/app/orders"
                  : `/app/orders/${completedOrderId}`
              }
            >
              View order
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="max-w-[64rem] mx-auto px-15 md:px-40 py-40">
        <div className="border border-line-subtle bg-raised p-20 text-center">
          <h1 className="type-heading-lg">Treatment approval unavailable</h1>
          <p className="type-body-sm text-ink-muted mt-6">
            This approval may have expired, been purchased, or belong to a
            different account.
          </p>
          <div className="mt-15 flex justify-center gap-7">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void offers.refetch()}
            >
              Check again
            </Button>
            <Button asChild>
              <Link to="/app">Return home</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (recoveringPurchase) {
    const resumePurchase = async () => {
      setError(null);
      try {
        const result = await resume.mutateAsync(offer.id);
        setCompletedOrderId(result.items[0]?.pharmacy_order_id ?? "complete");
      } catch (failure) {
        if (
          failure instanceof ApiError &&
          failure.code === "clinical_authorization_payment_closed"
        ) {
          setError(
            "The earlier unpaid attempt is closed. You can review the plan and payment method before trying again.",
          );
          await offers.refetch();
          return;
        }
        setError(
          failure instanceof Error
            ? failure.message
            : "We could not confirm the saved purchase yet. Check again or message the care team; do not start a second purchase.",
        );
      }
    };
    return (
      <div className="max-w-[64rem] mx-auto px-15 md:px-40 py-40">
        <section className="border border-info/30 bg-info-wash p-20">
          <p className="type-label-xs text-info">SAVED PURCHASE</p>
          <h1 className="type-heading-lg mt-6">
            Complete your {offer.product_label} purchase
          </h1>
          <p className="type-body-sm text-ink-secondary mt-8">
            We’ll check the original payment and finish linking your clinician’s
            approval to the order. This uses the same saved attempt, not a new
            purchase or a different card or plan.
          </p>
          {error && (
            <p role="alert" className="type-body-sm text-danger mt-10">
              {error}
            </p>
          )}
          <div className="mt-15 flex flex-wrap gap-8">
            <Button
              type="button"
              loading={resume.isPending}
              onClick={() => void resumePurchase()}
            >
              Check purchase status
            </Button>
            <Button asChild variant="secondary">
              <Link to="/app/clinic?message=1">Message care team</Link>
            </Button>
          </div>
        </section>
      </div>
    );
  }

  if (!product || !cartProduct) {
    return (
      <div className="max-w-[64rem] mx-auto px-15 md:px-40 py-40">
        <div className="border border-warning/30 bg-warning-wash p-18">
          <h1 className="type-heading-lg">Purchase setup is being completed</h1>
          <p className="type-body-sm text-ink-secondary mt-6">
            Your clinical approval is safe, but it does not currently resolve to
            one exact live shop listing. Message the care team for help before
            purchasing anything else.
          </p>
          <Button
            type="button"
            variant="secondary"
            className="mt-12"
            loading={offers.isFetching || marketplace.isFetching}
            onClick={() => {
              void Promise.all([offers.refetch(), marketplace.refetch()]);
            }}
          >
            Retry catalog
          </Button>
        </div>
      </div>
    );
  }

  const active = offer.status === "offered";
  const price = cartProduct.plan_prices?.[frequency] ?? cartProduct.price_cents;
  const submit = async () => {
    if (!paymentMethodId || !active || !price) return;
    setError(null);
    let purchaseAttempted = false;
    try {
      purchaseAttempted = true;
      const result = await purchase.mutateAsync(
        treatmentOfferPurchasePayload({
          sanityProductId: offer.sanity_product_id ?? product.id,
          offerId: offer.id,
          frequency,
          paymentMethodId,
        }),
      );
      setCompletedOrderId(result.items[0]?.pharmacy_order_id ?? "complete");
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "The purchase did not complete and your card was not intentionally retried. Review the details and try again.",
      );
      // A timeout can follow a successful/frozen payment. Refresh the durable
      // authorization before exposing editable payment choices again.
      if (purchaseAttempted) {
        setPurchaseNeedsVerification(true);
        await verifyPurchaseState();
      }
    }
  };

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Approved after your visit"
        description="Your clinician already reviewed this exact treatment with you. Confirm the plan below when you’re ready to purchase."
      />
      <div className="max-w-[76rem] mx-auto w-full px-15 md:px-40 py-25 flex flex-col gap-18">
        <section className="border border-line-subtle bg-raised">
          <div className="grid md:grid-cols-[14rem_minmax(0,1fr)]">
            <div className="bg-sunken min-h-[14rem] flex items-center justify-center overflow-hidden">
              {product.hero_image_url ? (
                <img
                  src={product.hero_image_url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <FileCheck2 size={36} className="text-ink-muted" />
              )}
            </div>
            <div className="p-16 min-w-0">
              <p className="type-label-xs text-success">CLINICIAN APPROVED</p>
              <h1 className="type-heading-xl mt-5">{offer.product_label}</h1>
              <p className="type-body-sm text-ink-secondary mt-5">
                {offer.medication_name}
              </p>
              {offer.dosage && (
                <p className="type-label-sm text-ink-muted mt-8">
                  DOSE · {offer.dosage}
                </p>
              )}
            </div>
          </div>
          <div className="border-t border-line-subtle p-15">
            <p className="type-label-xs text-ink-muted">
              PRESCRIPTION DIRECTIONS
            </p>
            <p className="type-body-sm mt-5 whitespace-pre-wrap">
              {offer.directions}
            </p>
          </div>
        </section>

        <section className="border border-success/30 bg-success-wash p-13 flex items-start gap-9">
          <ShieldCheck size={19} className="text-success shrink-0 mt-1" />
          <div>
            <p className="type-label-sm text-success">
              Clinical review complete
            </p>
            <p className="type-body-sm text-ink-secondary mt-3">
              This approval is tied to the medication and dose above. Buying it
              uses the prescription written after your visit; changing to a
              different product or dose requires clinician review.
            </p>
            {offer.expires_at && (
              <p className="text-11 text-ink-muted mt-5">
                Available through{" "}
                {new Date(offer.expires_at).toLocaleDateString()}.
              </p>
            )}
          </div>
        </section>

        <section className="border border-line-subtle bg-raised p-15 flex flex-col gap-12">
          <div>
            <p className="type-label-sm">Choose plan</p>
            <p className="text-11 text-ink-muted mt-2">
              This purchase contains only the treatment your clinician approved.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-7">
            {frequencies.map((option) => {
              const optionPrice =
                cartProduct.plan_prices?.[option] ?? cartProduct.price_cents;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={frequency === option}
                  onClick={() => setFrequency(option)}
                  className={`border p-11 text-left ${
                    frequency === option
                      ? "border-ink bg-ink text-page"
                      : "border-line bg-raised hover:bg-sunken"
                  }`}
                >
                  <span className="block type-label-sm">
                    {FREQUENCY_LABELS[option]}
                  </span>
                  <span className="block type-body-sm mt-4">
                    {money(optionPrice)} {FREQUENCY_CYCLE_LABELS[option]}
                  </span>
                </button>
              );
            })}
          </div>
          <PaymentMethodPicker
            selectedPmId={paymentMethodId}
            onSelect={setPaymentMethodId}
          />
          {error && (
            <p
              role="alert"
              className="border border-danger/30 bg-danger-wash p-9 type-body-sm text-danger"
            >
              {error}
            </p>
          )}
          {!active && (
            <p className="border border-warning/30 bg-warning-wash p-9 type-body-sm text-warning">
              This approval is {offer.status.replaceAll("_", " ")} and cannot be
              purchased. Message the care team if you need help.
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-10 border-t border-line-subtle pt-12">
            <div>
              <p className="type-label-xs text-ink-muted">TOTAL TODAY</p>
              <p className="type-heading-lg mt-2">{money(price)}</p>
            </div>
            <div className="w-full sm:w-auto sm:max-w-[36rem]">
              <Button
                type="button"
                loading={purchase.isPending}
                disabled={!active || !paymentMethodId || !price}
                onClick={() => void submit()}
              >
                Purchase approved treatment
              </Button>
              <CarePurchaseNote />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
